import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';

// Critical components loaded immediately
import PerformanceMonitor from './components/PerformanceMonitor';
import OptimizedImage from './components/OptimizedImage';
import Footer from './components/Footer';
import PWAInstallButton from './components/PWAInstallButton';
import usePaymentStatus from './hooks/usePaymentStatus';
import Testimonials from './components/Testimonials';
import { prewarmBackend } from './utils/prewarm';

// Preload critical components that users are likely to access immediately
const Signup = lazy(() => import('./components/Signup'));
const Login = lazy(() => import('./components/Login'));
const FAQ = lazy(() => import('./components/FAQ'));
const HairTypesGuide = lazy(() => import('./components/HairTypesGuide'));

// Lazy load less critical components for code splitting
const Questionnaire = lazy(() => import('./components/Questionnaire'));
const Submissions = lazy(() => import('./components/Submissions'));
const Account = lazy(() => import('./components/Account'));
const PaymentModal = lazy(() => import('./components/PaymentModal'));
const WhatYouGet = lazy(() => import('./components/WhatYouGet'));
const History = lazy(() => import('./components/History'));
const AdminSupport = lazy(() => import('./components/AdminSupport'));
const AnalyticsTest = lazy(() => import('./components/AnalyticsTest'));

// Legal pages - lazy loaded
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./components/TermsOfUse'));
const ContactUs = lazy(() => import('./components/ContactUs'));

// Note: Removed preloading to avoid React 18 Suspense synchronous update issues

// Debug environment variables
console.log('🔧 Environment Debug:', {
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  REACT_APP_SUPABASE_URL: process.env.REACT_APP_SUPABASE_URL,
  allEnvVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
});

// Import test for development
if (process.env.NODE_ENV === 'development') {
  import('./test-auth');
}

// Helper function for lazy analytics loading
const lazyTrack = (method, ...args) => {
  import('./utils/analytics').then((analytics) => {
    if (analytics[method]) {
      analytics[method](...args);
    }
  });
};

// PrivateRoute component to protect routes
const PrivateRoute = ({ children }) => {
  const { currentUser } = useAuth();
  console.log("PrivateRoute check, currentUser:", currentUser ? currentUser.email : null); // Log check
  // If no user, redirect to login
  return currentUser ? children : <Navigate to="/login" />;
};

// Analytics tracking component for page views (lazy loaded)
const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Lazy load analytics and track page views
    import('./utils/analytics').then(({ trackPageView }) => {
      trackPageView(document.title, window.location.href);
    });
  }, [location]);

  return null;
};

// Updated Home component with auth state and logout
const Home = () => {
  const { currentUser, logout, hasUnlimitedAccess } = useAuth();
  const navigate = useNavigate(); // Use navigate for logout redirect
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = React.useState(false);
  const mobileMenuRef = React.useRef(null);

  // Use payment status hook for enhanced payment verification
  const { hasValidPayment, paymentSession, refreshPaymentStatus } = usePaymentStatus();

  // Initialize analytics lazily to reduce initial bundle size
  useEffect(() => {
    import('./utils/analytics').then(({ initializeAnalytics }) => {
      initializeAnalytics();
    });
    // Prewarm backend on homepage mount
    prewarmBackend('home_mount');
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      console.log("Logout successful, navigating to login.");
      navigate('/login'); // Redirect to login page after logout
      // Hard-refresh as a fallback to clear any persisted UI state
      setTimeout(() => {
        try { window.location.reload(); } catch (_) {}
      }, 150);
    } catch (error) {
      console.error("Failed to log out:", error);
      // As a last resort, force a reload to ensure session is cleared client-side
      try { window.location.reload(); } catch (_) {}
    }
  };

  const handleViewResults = async (e) => {
    console.log("View Results clicked", {
      currentUser: currentUser?.email,
      isMobile: window.innerWidth <= 768,
      target: e.target,
      timestamp: new Date().toISOString()
    });
    e.preventDefault();
    e.stopPropagation();
    // Kick off a best-effort prewarm before navigating
    try { prewarmBackend('view_results'); } catch (_) {}
    navigate('/submissions');
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleStartAnalysis = async () => {
    lazyTrack('trackCTAClick', 'start_hair_analysis', 'homepage');
    lazyTrack('trackAnalysisStart');
    // Allow users to proceed to the questionnaire without upfront payment
    navigate('/questionnaire');
    // Payment gating disabled for now, keeping code for future use
    return;
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    console.log('Payment successful:', paymentIntent);
    lazyTrack('trackPaymentComplete', 'stripe', 9.99);
    setIsPaymentModalOpen(false);

    // NEW: Refresh payment status to get the latest payment session
    await refreshPaymentStatus();

    navigate('/questionnaire');
  };

  const handlePaymentCancel = () => {
    setIsPaymentModalOpen(false);
  };

  // Close mobile menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden">
      {/* Hero image background - optimized for LCP */}
      <div className="absolute top-0 left-0 w-full h-screen overflow-hidden z-0">
        <div className="absolute inset-0 bg-dark bg-opacity-70"></div>
        <OptimizedImage
          src="/images/image_14.png"
          alt="Stylish hair background"
          className="absolute object-cover w-full h-full z-0"
          priority={true}
          sizes="(max-width: 320px) 320px, (max-width: 480px) 480px, (max-width: 768px) 768px, (max-width: 1200px) 1200px, 100vw"
          style={{ width: '100%', height: '100vh' }}
          imgClassName="object-[center_top]"
          imgStyle={{ objectPosition: 'center top' }}
        />
      </div>

	      {/* Secondary background below hero using image_8 */}
	      <div className="absolute left-0 right-0 top-[100vh] bottom-0 z-0">
	        <div className="absolute inset-0 bg-dark bg-opacity-70"></div>
	        <OptimizedImage
	          src="/images/image_8.png"
	          alt="Background continuation"
	          className="absolute object-cover w-full h-full z-0"
	          sizes="100vw"
	        />
	      </div>


	      {/* Top-left Instagram icon (homepage only) */}
	      <a
	        href="https://www.instagram.com/hairalyzer?igsh=MWYxMm96MGswenQxNw=="
	        target="_blank"
	        rel="noopener noreferrer"
	        aria-label="Instagram"
	        title="Instagram"
	        className="absolute top-6 left-6 z-50 text-white/80 hover:text-white transition-colors"
	        style={{ pointerEvents: 'auto' }}
	      >
	        {/* Clean outline icon */}
	        <svg
	          xmlns="http://www.w3.org/2000/svg"
	          viewBox="0 0 24 24"
	          fill="none"
	          stroke="currentColor"
	          strokeWidth="1.6"
	          className="w-7 h-7"
	          role="img"
	        >
	          <title>Instagram</title>
	          <rect x="3" y="3" width="18" height="18" rx="5" ry="5"></rect>
	          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
	          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
	        </svg>
	      </a>

      {/* Desktop Navigation */}
      <div className="absolute top-6 right-6 hidden md:flex items-center space-x-4 z-50">
        {currentUser ? (
          <>
            <span className="text-white font-medium">Hello, {currentUser.email.split('@')[0]}</span>
            <Link
              to="/hair-types-guide"
              className="px-4 py-2 rounded-full bg-white bg-opacity-20 backdrop-filter backdrop-blur-sm text-white font-medium hover:bg-opacity-30 transition-all duration-300 border border-white border-opacity-30"
            >
              Hair Guide
            </Link>
            <button
              onClick={handleViewResults}
              className="px-4 py-2 rounded-full bg-white bg-opacity-20 backdrop-filter backdrop-blur-sm text-white font-medium hover:bg-opacity-30 transition-all duration-300 border border-white border-opacity-30 cursor-pointer relative z-50"
              style={{ pointerEvents: 'auto' }}
            >
              View Results
            </button>
            <button
              onClick={() => navigate('/account')}
              className="px-4 py-2 rounded-full bg-white bg-opacity-20 backdrop-filter backdrop-blur-sm text-white font-medium hover:bg-opacity-30 transition-all duration-300 border border-white border-opacity-30"
            >
              My Account
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 rounded-full bg-white bg-opacity-20 backdrop-filter backdrop-blur-sm text-white font-medium hover:bg-opacity-30 transition-all duration-300 hover:text-accent"
            >
              Log Out
            </button>
          </>
        ) : (
          <>
            <Link
              to="/login"
              className="px-4 py-2 rounded-full bg-white bg-opacity-20 backdrop-filter backdrop-blur-sm text-white font-medium hover:bg-opacity-30 transition-all duration-300 border border-white border-opacity-30"
            >
              Log In
            </Link>
            <Link
              to="/signup"
              className="px-4 py-2 rounded-full bg-gradient-to-r from-accent to-primary text-white font-medium hover:shadow-md transition-all duration-300"
            >
              Sign Up
            </Link>
          </>
        )}
      </div>

      {/* Mobile Hamburger Menu */}
      <div ref={mobileMenuRef} className="absolute top-6 right-6 md:hidden z-50">
        <button
          onClick={toggleMobileMenu}
          className="p-3 rounded-full bg-white bg-opacity-20 backdrop-filter backdrop-blur-sm text-white hover:bg-opacity-30 transition-all duration-300 touch-manipulation"
          style={{ WebkitTapHighlightColor: 'transparent' }}
          aria-label="Toggle menu"
        >
          <div className="w-6 h-6 flex flex-col justify-center items-center">
            <span className={`block w-5 h-0.5 bg-white transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : 'mb-1'}`}></span>
            <span className={`block w-5 h-0.5 bg-white transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : 'mb-1'}`}></span>
            <span className={`block w-5 h-0.5 bg-white transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
          </div>
        </button>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="absolute top-16 right-0 w-64 bg-white bg-opacity-95 backdrop-filter backdrop-blur-sm rounded-xl shadow-lg border border-white border-opacity-30 overflow-hidden mobile-menu-dropdown">
            {currentUser ? (
              <>
                <div className="px-4 py-3 border-b border-gray-200">
                  <p className="text-sm text-gray-600">Hello,</p>
                  <p className="font-medium text-gray-800">{currentUser.email.split('@')[0]}</p>
                </div>
                <button
                  onClick={handleViewResults}
                  className="w-full px-4 py-3 text-left text-gray-800 hover:bg-gray-100 transition-colors duration-200 border-b border-gray-200"
                >
                  📊 View Results
                </button>
                <Link
                  to="/hair-types-guide"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full px-4 py-3 text-left text-gray-800 hover:bg-gray-100 transition-colors duration-200 border-b border-gray-200"
                >
                  📚 Hair Guide
                </Link>
                <button
                  onClick={() => {
                    navigate('/account');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full px-4 py-3 text-left text-gray-800 hover:bg-gray-100 transition-colors duration-200 border-b border-gray-200"
                >
                  👤 My Account
                </button>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full px-4 py-3 text-left text-gray-800 hover:bg-gray-100 transition-colors duration-200"
                >
                  🚪 Log Out
                </button>
              </>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-gray-200">
                  <PWAInstallButton className="w-full justify-center text-sm" />
                </div>
                <Link
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full px-4 py-3 text-left text-gray-800 hover:bg-gray-100 transition-colors duration-200 border-b border-gray-200"
                >
                  🔑 Log In
                </Link>
                <Link
                  to="/signup"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full px-4 py-3 text-left text-gray-800 hover:bg-gray-100 transition-colors duration-200"
                >
                  ✨ Sign Up
                </Link>
              </>
            )}
          </div>
        )}
      </div>

      <div className="relative z-10 text-center max-w-4xl px-8 py-16 animate-fade-in mx-4">
        <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent drop-shadow-lg">Hairalyzer</h1>
        <p className="text-xl text-white mb-10 max-w-2xl mx-auto leading-relaxed">Hairalyzer uses AI to scan and analyze hair health, providing personalized assessments, growth forecasts, and tailored product recommendations.</p>

        {/* Conditional content based on login status */}
        {currentUser ? (
          <div className="text-center">
            {/* NEW: Session Recovery Notification */}
            {hasValidPayment && paymentSession && (
              <div className="mb-6 p-4 bg-green-100 border border-green-300 rounded-lg">
                <div className="flex items-center justify-center mb-2">
                  <span className="text-green-600 text-xl mr-2">✅</span>
                  <h3 className="text-green-800 font-semibold">Resume Your Analysis</h3>
                </div>
                <p className="text-green-700 text-sm mb-3">
                  You have a paid session ready to continue. No additional payment needed!
                </p>
                <button
                  onClick={handleStartAnalysis}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Continue Analysis
                </button>
              </div>
            )}

            <button
              onClick={handleStartAnalysis}
              className="px-8 py-4 rounded-full bg-gradient-to-r from-accent to-primary text-white font-semibold text-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
            >
              <span className="mr-2">
                {hasValidPayment ? 'Continue Hair Analysis' : 'Start Hair Analysis'}
              </span>
              {hasUnlimitedAccess() ? (
                <span className="text-2xl font-bold">FREE ✨</span>
              ) : hasValidPayment ? (
                <span className="text-2xl font-bold">PAID ✅</span>
              ) : (
                <span className="text-2xl font-bold">$9.99</span>
              )}
            </button>

            {/* What You Get button */}
            <div className="mt-4">
              <Link
                to="/what-you-get"
                className="inline-block px-6 py-3 rounded-full bg-white bg-opacity-20 backdrop-filter backdrop-blur-sm text-white font-medium hover:bg-opacity-30 transition-all duration-300 border border-white border-opacity-30"
              >
                💎 What You Get for $9.99
              </Link>
            </div>


          </div>
        ) : (
          <div className="mt-8">
            <div className="mb-10 p-6 bg-black bg-opacity-30 backdrop-filter backdrop-blur-sm rounded-xl border border-white border-opacity-20">
              <p className="text-lg text-white mb-0">Get your personalized hair analysis and expert recommendations instantly</p>
            </div>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <Link
                to="/signup"
                onClick={() => lazyTrack('trackCTAClick', 'start_hair_analysis_unauthed', 'homepage')}
                className="px-8 py-4 rounded-full bg-gradient-to-r from-accent to-primary text-white font-semibold text-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
              >
                <span className="mr-2">Start Hair Analysis</span>
                <span className="text-2xl font-bold">$9.99</span>
              </Link>
              <Link
                to="/what-you-get"
                className="inline-block px-6 py-3 rounded-full bg-white bg-opacity-20 backdrop-filter backdrop-blur-sm text-white font-medium hover:bg-opacity-30 transition-all duration-300 border border-white border-opacity-30"
              >
                💎 What You Get
              </Link>
            </div>
            <div className="mt-6 text-center">
              <p className="text-white">
                Already have an account?{' '}
                <Link
                  to="/login"
                  onClick={() => lazyTrack('trackNavigation', 'login', 'homepage_prompt')}
                  className="font-medium text-accent hover:underline"
                >
                  Log In
                </Link>
              </p>
            </div>
          </div>
        )}

        {/* How It Works section - now visible to all users */}
        <div className="mt-12 p-4 sm:p-8 bg-black bg-opacity-30 backdrop-filter backdrop-blur-sm rounded-none sm:rounded-2xl border border-white border-opacity-20 -mx-8 sm:mx-0">
          <h3 className="text-xl font-semibold text-white mb-4">How It Works</h3>
          <div className="grid md:grid-cols-3 gap-6 text-left">
            <div className="p-4 bg-white rounded-xl shadow-soft">
              <div className="w-10 h-10 rounded-full bg-primary bg-opacity-10 flex items-center justify-center text-primary font-bold mb-3">1</div>
              <h4 className="font-medium mb-2">Answer Questions</h4>
              <p className="text-sm text-gray-600">Tell us about your hair concerns, routine, and products you currently use</p>
            </div>
            <div className="p-4 bg-white rounded-xl shadow-soft">
              <div className="w-10 h-10 rounded-full bg-primary bg-opacity-10 flex items-center justify-center text-primary font-bold mb-3">2</div>
              <h4 className="font-medium mb-2">AI 3 Photos Hair Analysis</h4>
              <p className="text-sm text-gray-600">Our rapid three-angle scanning method is designed to capture every detail of your hair’s condition.</p>
            </div>
            <div className="p-4 bg-white rounded-xl shadow-soft">
              <div className="w-10 h-10 rounded-full bg-primary bg-opacity-10 flex items-center justify-center text-primary font-bold mb-3">3</div>
              <h4 className="font-medium mb-2">Personalized Hair Health Diagnostics</h4>
              <p className="text-sm text-gray-600">Accurate evaluation of hair health, powered by AI algorithms and other essential data.</p>
            </div>
          </div>

          {/* FAQ Button */}
          <div className="mt-6 text-center">
            <Link
              to="/faq"
              className="inline-flex items-center px-6 py-3 bg-white bg-opacity-20 text-white font-medium rounded-full hover:bg-opacity-30 transition-all duration-300 transform hover:scale-105 backdrop-blur-sm border border-white border-opacity-30"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              Frequently Asked Questions
            </Link>
          </div>
        </div>


        {/* Free Hair Guide block (immediately after How It Works, logged-out only) */}
        {!currentUser && (
          <div className="mt-8 p-6 bg-black bg-opacity-30 backdrop-filter backdrop-blur-sm rounded-xl border border-white border-opacity-20">
            <h3 className="text-2xl font-semibold text-white mb-2">Your Free Hair Care Starter Guide</h3>
            <p className="text-white/90 mb-4">It’s our gift to you to help you start your hair journey the right way.</p>
            <Link
              to="/signup"
              onClick={() => lazyTrack('lead', 'free_hair_types_guide', 'homepage')}
              className="inline-block px-6 py-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium hover:shadow-lg transition"
            >
              Free Hair Types Guide
            </Link>
          </div>
        )}

        {/* Testimonials Section */}
        <Testimonials />


        {/* Security Badge - below How It Works section */}
        <div className="mt-6 p-4 bg-white bg-opacity-10 backdrop-filter backdrop-blur-sm rounded-xl border border-white border-opacity-20">
          <div className="flex items-center justify-center text-white">
            <span className="text-2xl mr-2">🔒</span>
            <span className="font-medium">100% secure & private</span>
          </div>
        </div>

      </div>



      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={handlePaymentCancel}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

// Main App component wrapped with AuthProvider
function App() {
  try {
    return (
      <AuthProvider> {/* Wrap the entire app with AuthProvider */}
        <Router>
          <AnalyticsTracker />
          <PerformanceMonitor />
          {/* Removed the old nav bar */}
          <div className="min-h-screen flex flex-col">
            <div className="flex-grow">
              <Suspense fallback={null}>
                <Routes>
            <Route path="/" element={<Home />} />
            <Route
              path="/what-you-get"
              element={
                  <WhatYouGet />
              }
            />
            <Route path="/signup" element={<Signup />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/questionnaire"
              element={
                <PrivateRoute> {/* Protect Questionnaire */}
                  <Questionnaire />
                </PrivateRoute>
              }
            />
            <Route
              path="/submissions"
              element={
                <PrivateRoute> {/* Protect Submissions */}
                  <Submissions />
                </PrivateRoute>
              }
            />
            <Route
              path="/account"
              element={
                <PrivateRoute> {/* Protect Account */}
                  <Account />
                </PrivateRoute>
              }
            />
            <Route
              path="/history"
              element={
                <PrivateRoute> {/* Protect History */}
                  <History />
                </PrivateRoute>
              }
            />
            <Route
              path="/faq"
              element={<FAQ />}
            />
            <Route
              path="/hair-types-guide"
              element={<HairTypesGuide />}
            />
            <Route
              path="/admin/support"
              element={<AdminSupport />}
            />

            {/* Legal Pages */}
            <Route
              path="/privacy-policy"
              element={<PrivacyPolicy />}
            />
            <Route
              path="/terms-of-use"
              element={<TermsOfUse />}
            />
            <Route
              path="/contact"
              element={<ContactUs />}
            />

            {/* Add a catch-all route or redirect for unknown paths */}
            <Route path="*" element={<Navigate to="/" />} />
                </Routes>
              </Suspense>
            </div>

            {/* Footer - appears on all pages */}
            <Footer />
          </div>

          {/* Analytics Test Component (Development Only) */}
          <Suspense fallback={<div></div>}>
            <AnalyticsTest />
          </Suspense>
        </Router>
      </AuthProvider>
    );
  } catch (error) {
    console.error('Error rendering app:', error);

    // In development, show more detailed error info
    if (process.env.NODE_ENV === 'development') {
      console.error('Development error details:', error);
    }

    // Fallback UI
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary to-accent">
        <div className="text-center text-white p-8">
          <h1 className="text-4xl font-bold mb-4">Hairalyze</h1>
          <p className="text-lg mb-2">Something went wrong loading the application.</p>
          <p className="text-sm opacity-75 mb-4">Please refresh the page to try again.</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-white text-primary px-6 py-2 rounded-full font-semibold hover:bg-gray-100 transition-colors"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }
}

export default App;
